/**
 * sayText.ts
 * SayTextノードの定義
 * 前のノードから受け取ったテキストを実機またはブラウザで読み上げるノード
 */
import type { RobotCommandResult } from '@/connection/BatchRobotService';
import type { Logger, NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import { SayTextNodeView } from '../views/SayTextNode';
import { SayTextSettings } from '../settings/SayTextSettings';
import { nextFlowRequestId } from '../../atoms/flowRequestId';
import { resolveSpeechRequest } from '../../speechPlan';
import { playSpeechAudio } from '@/speech/speechAudioContext';
import {
  DEFAULT_SAY_TEXT_PITCH_SCALE,
  DEFAULT_SAY_TEXT_SPEAKER,
  DEFAULT_SAY_TEXT_SPEED_SCALE,
} from '../../config/sayTextVoice';

const SAY_TEXT_TIMEOUT_BUFFER_MS = 2000;

/**
 * SayTextノードの定義
 * - パレットに表示してユーザーがドラッグ配置できる
 * - 実行時はctx.lastValueを読み取り、実行先での発話完了を待つ
 * - TextEditが先にないとエラーになる
 */
const logBatchResult = (log: Logger, results: RobotCommandResult[]) => {
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      log(`[Say Text][${result.robotId}] 成功`);
      return;
    }
    log(`[Say Text][${result.robotId}] ${result.status}: ${result.message ?? result.errorCode ?? '不明なエラー'}`);
  });
};

export const sayTextDefinition: NodeDefinition<'sayText'> = {
  label: 'Say Text',
  showInPalette: true,      // パレットに表示
  paletteColor: 'bg-sky-100 border-sky-500 text-sky-700',
  miniMapColor: '#0ea5e9',  // スカイブルー
  defaultData: {
    label: 'Say Text',
    speaker: DEFAULT_SAY_TEXT_SPEAKER,
    speedScale: DEFAULT_SAY_TEXT_SPEED_SCALE,
    pitchScale: DEFAULT_SAY_TEXT_PITCH_SCALE,
  },
  view: SayTextNodeView,
  settings: SayTextSettings,
  run: async (node, ctx, log) => {
    const request = resolveSpeechRequest(ctx.lastValue, node.data);
    if (typeof request === 'string') {
      log(`[Say Text] エラー: ${request}`);
      ctx.onExecutionEvent?.({ type: 'node-error', nodeId: node.id, nodeKind: 'sayText', message: request });
      return { nextHandle: null };
    }
    const { text: msg } = request;
    try {
      const speechAudio = await ctx.speechPrefetch?.get(request);
      ctx.signal?.throwIfAborted();
      // VirtualRobot でふきだし表示を待つ
      const displayDuration = Math.max(msg.length * 100, 1500);  // 文字数に応じて表示時間を計算（最小1.5秒）
      const virtualDone: Promise<void> = ctx.setSayText
        ? new Promise<void>((resolve, reject) => {
            let settled = false;
            const finish = () => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeoutId);
              ctx.signal?.removeEventListener('abort', abort);
              resolve();
            };
            const abort = () => {
              if (settled) return;
              settled = true;
              window.clearTimeout(timeoutId);
              ctx.signal?.removeEventListener('abort', abort);
              reject(new DOMException('Flow execution aborted', 'AbortError'));
            };
            const timeoutId = window.setTimeout(finish, displayDuration + SAY_TEXT_TIMEOUT_BUFFER_MS);
            if (ctx.signal?.aborted) abort();
            else ctx.signal?.addEventListener('abort', abort, { once: true });
            ctx.setSayText!({
              text: msg,
              duration: displayDuration,
              requestId: nextFlowRequestId(),
              onComplete: finish,
            });
          })
        : Promise.resolve();

      if (ctx.robotService) {
        const [, summary] = await Promise.all([
          virtualDone,
          ctx.robotService.speak(
            request,
            { nodeId: node.id, signal: ctx.signal, ...(speechAudio ? { speechAudio } : {}) },
          ),
        ]);
        logBatchResult(log, summary.results);
        log(`[Say Text] 発話: ${msg}（${summary.results.filter((result) => result.status === 'fulfilled').length}/${summary.results.length}台）`);
        ctx.onExecutionEvent?.({ type: 'speech', nodeId: node.id, text: msg });
        return;
      }
      if (speechAudio || ctx.playSimulationSpeech) {
        await Promise.all([
          virtualDone,
          speechAudio
            ? playSpeechAudio(speechAudio.slice(0), ctx.signal)
            : ctx.playSimulationSpeech!(request, ctx.signal),
        ]);
        log(`[Say Text] 発話: ${msg}`);
        ctx.onExecutionEvent?.({ type: 'speech', nodeId: node.id, text: msg });
        return;
      }
      await virtualDone;
      console.log(msg);
      log(`[Say Text] console.log: ${msg}`);
      ctx.onExecutionEvent?.({ type: 'speech', nodeId: node.id, text: msg });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      const message = error instanceof Error ? error.message : String(error);
      ctx.onExecutionEvent?.({ type: 'node-error', nodeId: node.id, nodeKind: 'sayText', message });
      log(`[Say Text] エラー: ${String(error)}`);
      return { nextHandle: null };
    }
  },
  help: {
    title: [t('Say Text（ことばを'), r('出', 'だ'), t('す）')],
    description: [
      r('受', 'う'),
      t('け'),
      r('取', 'と'),
      t('った「ことば」を、ロボットが'),
      r('声', 'こえ'),
      t('に'),
      r('出', 'だ'),
      t('して'),
      r('話', 'はな'),
      t('します。'),
      r('話', 'はな'),
      t('し'),
      r('終', 'お'),
      t('わってから、つぎのカードへ'),
      r('進', 'すす'),
      t('みます。'),
    ],
  },
};
